import React, { Fragment, useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Axios } from "api/axios";
import { toast } from "react-toastify";
import CancelBackButton from "components/Buttons/CancelBackButton";
import PageContainer from "components/PageContainer/PageContainer";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";

interface SupplierDetails {
  supplier_name: string;
  supplier_code: string;
  email: string;
  phone_no: string;
  address: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  gst_in: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  transporter_details: Array<{
    transporter_name?: string;
    transporter_contact_person_name?: string;
    transporter_contact_person_phone_no?: string;
  }>;
}

const SupplierViewDetails: React.FC = () => {
  document.title = "Supplier View Details";
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplierDetails, setSupplierDetails] =
    useState<SupplierDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchSupplierData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const response = await Axios.get(`/master/supplier/${id}/`);
        if (response?.data?.data) {
          setSupplierDetails(response.data.data);
        } else {
          toast.error("Failed to fetch supplier details");
        }
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Failed to fetch supplier details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierData();
  }, [id]);

  const handleCancel = () => {
    navigate("/supplier-list");
  };

  if (loading) {
    return (
      <Fragment>
        <div
          className="page-content px-4"
          style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
        >
          <Container
            fluid
            style={{
              backgroundColor: "#fff",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0 0 15px rgba(0,0,0,0.1)",
            }}
          >
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Container>
        </div>
      </Fragment>
    );
  }

  return (
    <PageContainer>
      <FormHeader
        title="Supplier Information"
        firstLabel="Supplier"
        secondLabel="View Details"
        handleArrowClick={() => navigate("/supplier-list")}
      />

      <Row>
        <Col lg={12}>
          <div className="mb-4">
            <h6 className="mb-3">
              <strong>Supplier Information</strong>
            </h6>
            <Row>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Supplier Name:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.supplier_name || "—"}
                  </div>
                </div>
              </Col>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Supplier Code:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.supplier_code || "—"}
                  </div>
                </div>
              </Col>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    GSTIN:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.gst_in || "—"}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col lg={12}>
          <div className="mb-4">
            <h6 className="mb-3">
              <strong>Contact Details</strong>
            </h6>
            <Row>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Email:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.email || "—"}
                  </div>
                </div>
              </Col>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Phone Number:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.phone_no
                      ? `+91-${supplierDetails.phone_no}`
                      : "—"}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col lg={12}>
          <div className="mb-4">
            <h6 className="mb-3">
              <strong>Contact Person</strong>
            </h6>
            <Row>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Contact Person Name:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.contact_person_name || "—"}
                  </div>
                </div>
              </Col>
              <Col lg={3} className="mb-3">
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: "500", color: "#495057" }}
                  >
                    Phone No:
                  </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      backgroundColor: "#f8f9fa",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {supplierDetails?.contact_person_phone_no
                      ? `+91-${supplierDetails.contact_person_phone_no}`
                      : "—"}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col lg={12}>
          <div className="mb-4">
            <h6 className="mb-3">
              <strong>Transporter Details</strong>
            </h6>
            {supplierDetails?.transporter_details &&
            supplierDetails.transporter_details.length > 0 ? (
              supplierDetails.transporter_details.map((transporter, index) => (
                <Row key={index} className="mb-3">
                  <Col lg={3} className="mb-3">
                    <div>
                      <label
                        className="form-label"
                        style={{ fontWeight: "500", color: "#495057" }}
                      >
                        Transporter Person Name {index + 1}:
                      </label>
                      <div
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          minHeight: "38px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {transporter?.transporter_name || "—"}
                      </div>
                    </div>
                  </Col>
                  <Col lg={3} className="mb-3">
                    <div>
                      <label
                        className="form-label"
                        style={{ fontWeight: "500", color: "#495057" }}
                      >
                        Contact Person Name:
                      </label>
                      <div
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          minHeight: "38px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {transporter?.transporter_contact_person_name || "—"}
                      </div>
                    </div>
                  </Col>
                  <Col lg={3} className="mb-3">
                    <div>
                      <label
                        className="form-label"
                        style={{ fontWeight: "500", color: "#495057" }}
                      >
                        Phone No:
                      </label>
                      <div
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          minHeight: "38px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {transporter?.transporter_contact_person_phone_no
                          ? `+91-${transporter.transporter_contact_person_phone_no}`
                          : "—"}
                      </div>
                    </div>
                  </Col>
                </Row>
              ))
            ) : (
              <Row>
                <Col lg={3} className="mb-3">
                  <div>
                    <label
                      className="form-label"
                      style={{ fontWeight: "500", color: "#495057" }}
                    >
                      Transporter Person Name 1:
                    </label>
                    <div
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        backgroundColor: "#f8f9fa",
                        minHeight: "38px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      —
                    </div>
                  </div>
                </Col>
                <Col lg={3} className="mb-3">
                  <div>
                    <label
                      className="form-label"
                      style={{ fontWeight: "500", color: "#495057" }}
                    >
                      Contact Person Name:
                    </label>
                    <div
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        backgroundColor: "#f8f9fa",
                        minHeight: "38px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      —
                    </div>
                  </div>
                </Col>
                <Col lg={3} className="mb-3">
                  <div>
                    <label
                      className="form-label"
                      style={{ fontWeight: "500", color: "#495057" }}
                    >
                      Phone No:
                    </label>
                    <div
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        backgroundColor: "#f8f9fa",
                        minHeight: "38px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      —
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </Col>
      </Row>

      <div className="d-flex justify-content-center gap-3 py-3">
        {/* <Button
                            variant="light"
                            onClick={handleCancel}
                            style={{ backgroundColor: "white", border: "1px solid #ced4da", color: "#212529" }}
                        >
                            Cancel
                        </Button> */}
        <CancelBackButton to="/supplier-list" label="Cancel" />
      </div>
    </PageContainer>
  );
};

export default SupplierViewDetails;
